/** Parses natural language strings to extract task properties like projects, contexts, and dates. */
// @ts-check
import { parseDateString } from '@/features/tasks/utils/data/dateParser.js';
import { parseDurationInput } from '@/utils/timeUtils.js';

/** Matches ~duration tags, e.g. ~30m, ~1u, ~1h30, ~90. Capture group 1 is passed to parseDurationInput. */
const DURATION_TAG_REGEX = /~(\d+(?:[.,]\d+)?(?:[hu](?:\d+m?)?|m)?)/;

/**
 * Capitalizes the first letter of a string.
 * @param {string} s The string to capitalize.
 * @returns {string} The capitalized string.
 */
const capitalize = (s) => {
  if (typeof s !== 'string' || s.length === 0) {
    return '';
  }
  return s.charAt(0).toUpperCase() + s.slice(1);
};

/**
 * Converts a recurrence tag string into the correct database object structure.
 * @param {string} tag - The raw tag without the prefix (e.g., 'wekelijks', '3w', '14-dagen')
 * @returns {{ interval: number, frequency: 'days'|'weeks'|'months'|'years' } | null}
 */
const parseRecurrenceTag = (tag) => {
  const t = tag.toLowerCase();

  // Check simple cases first
  /** @type {Record<string, { interval: number, frequency: 'days'|'weeks'|'months'|'years' }>} */
  const simpleMappings = {
    dagelijks: { interval: 1, frequency: 'days' },
    wekelijks: { interval: 1, frequency: 'weeks' },
    maandelijks: { interval: 1, frequency: 'months' },
    jaarlijks: { interval: 1, frequency: 'years' },
  };

  if (simpleMappings[t]) {
    return simpleMappings[t];
  }

  const result = parseNumericTag(t);
  return result;
};

/**
 * @param {string} t
 * @returns {{ interval: number, frequency: 'days'|'weeks'|'months'|'years' } | null}
 */
const parseNumericTag = (t) => {
  const match = t.match(/^(\d+)-?(d|w|m|j|dagen|weken|maanden|jaar|jaren)$/);

  if (!match) {
    return null;
  }

  /** @type {Record<string, 'days'|'weeks'|'months'|'years'>} */
  const unitFrequencyMap = {
    d: 'days',
    dagen: 'days',
    w: 'weeks',
    weken: 'weeks',
    m: 'months',
    maanden: 'months',
    j: 'years',
    jaar: 'years',
    jaren: 'years',
  };

  const interval = parseInt(match[1], 10);
  const unit = match[2];
  const frequency = unitFrequencyMap[unit];

  return { interval, frequency };
};

/**
 * Parses a task input string to extract title, projects, contexts, person, and date.
 * Projects are identified by `#`, contexts by `@`, a person by `+`, recurrence by `*` and dates by `>`.
 * Tags can contain letters, numbers, underscores, and hyphens.
 *
 * ### Tag rules
 * - **Multiple** `#` and `@` tags are supported: all matches are collected into arrays.
 * - **Person** (`+`) is a singleton: only the **first** `+tag` is used. Intentional — a
 *   task is assigned to one person.
 * - **Recurrence** (`*`) is a singleton: only the **first** *tag` is used.
 * - **Date** (`>`) is extracted first and removed from the string before title cleaning,
 *   so date text never leaks into the title.
 * - **Duration** (`~`) is a singleton: only the **first** `~tag` is used (e.g. `~30m`,
 *   `~1u`, `~1h30`). Parsed via `parseDurationInput`; invalid tags stay in the title.
 * - Tags are stripped from the input in this order: date → duration → projects → contexts → person.
 *   The remaining text becomes the `title` (first letter capitalised).
 *
 * @param {string|null|undefined} input - Raw text entered by the user.
 * @returns {Partial<NewTask>}
 */
export const parseTaskInput = (input) => {
  if (!input) {
    return {
      title: '',
      projects: [],
      contexts: [],
      projectNames: {},
      contextNames: {},
      person: null,
      recurrence: null,
      date: null,
    };
  }

  const { processedInput: afterDate, date } = extractDate(input);
  const { processedInput, duration } = extractDuration(afterDate);
  const { projects, contexts, projectNames, contextNames, person, recurrence } =
    extractTags(processedInput);
  const title = capitalize(cleanupTitle(processedInput));

  const result = {
    title,
    projects,
    contexts,
    projectNames,
    contextNames,
    person,
    recurrence,
    date,
  };
  if (duration !== null) {
    result.duration = duration;
  }
  return result;
};

/**
 * @param {string} input
 * @returns {{ processedInput: string, date: string|null }}
 */
const extractDate = (input) => {
  const dateRegex = />(\S+)/;
  const dateMatch = input.match(dateRegex);

  if (dateMatch && dateMatch[1]) {
    const dateString = dateMatch[1];
    const date = parseDateString(dateString);
    const processedInput = input.replace(dateMatch[0], '').trim();
    return { processedInput, date };
  }

  return { processedInput: input, date: null };
};

/**
 * @param {string} input
 * @returns {{ processedInput: string, duration: number|null }}
 */
const extractDuration = (input) => {
  const match = input.match(DURATION_TAG_REGEX);
  if (!match) {
    return { processedInput: input, duration: null };
  }

  const duration = parseDurationInput(match[1]);
  if (duration === null) {
    return { processedInput: input, duration: null };
  }

  const processedInput = input.replace(match[0], '').trim();
  return { processedInput, duration };
};

/**
 * @param {string} processedInput
 */
/**
 * @param {RegExp} tagRegex
 * @param {string} processedInput
 * @returns {{ ids: string[], names: Record<string, string> }}
 */
const collectTagIdsAndNames = (tagRegex, processedInput) => {
  const ids = [];
  /** @type {Record<string, string>} */
  const names = {};
  const matches = processedInput.match(tagRegex) || [];
  for (const match of matches) {
    const spelling = match.substring(1);
    const id = spelling.toLowerCase();
    ids.push(id);
    if (!(id in names)) {
      names[id] = spelling;
    }
  }
  return { ids, names };
};

const extractTags = (processedInput) => {
  const projectRegex = /#([\w-]+)/g;
  const contextRegex = /@([\w-]+)/g;
  const personRegex = /\+([\w-]+)/g;
  const recurrenceRegex = /\*([\w-]+)/g;

  const { ids: projects, names: projectNames } = collectTagIdsAndNames(
    projectRegex,
    processedInput,
  );
  const { ids: contexts, names: contextNames } = collectTagIdsAndNames(
    contextRegex,
    processedInput,
  );

  const personMatch = processedInput.match(personRegex);
  const person = personMatch ? personMatch[0].substring(1) : null;

  const recurrenceMatch = processedInput.match(recurrenceRegex);
  const recurrence = recurrenceMatch
    ? parseRecurrenceTag(recurrenceMatch[0].substring(1))
    : null;

  return { projects, contexts, projectNames, contextNames, person, recurrence };
};

/**
 * @param {string} input
 * @returns {string}
 */
const cleanupTitle = (input) => {
  const projectRegex = /#([\w-]+)/g;
  const contextRegex = /@([\w-]+)/g;
  const personRegex = /\+([\w-]+)/g;
  const recurrenceRegex = /\*([\w-]+)/g;

  return input
    .replace(projectRegex, '')
    .replace(contextRegex, '')
    .replace(personRegex, '')
    .replace(recurrenceRegex, '')
    .replace(/\s\s+/g, ' ')
    .trim();
};
